import React, { lazy, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { Title } from "@vds/typography";
import { Button } from "@vds/buttons";
import { MediumText, SLNotification, SmallText, Spacer } from "../../components/core/utility";
import { getWorkTicketMaterialList } from "../../services/C4S Decommission - Replacement/getWorkTicketMaterialList";
import moment from "moment";
import { ATPCheck } from "../../services/C4S Decommission - Replacement/ATPCheck";
import { Loader } from "@vds/loaders";
import { groupBy, uniqBy } from "lodash";
import { switchTab } from "./utils";
import { cancelWorkticket } from "../../services/C4S Decommission - Replacement/cancelWorkticket";
import { C4SContext } from "../../context/c4sContext";
import { ViewVehicleList } from "../../services/C4S Decommission - Replacement/viewVehicleList";
import { getExchangeCode } from "../../../constants/ProcessingCodeConstants";
import { CreateWorkTicketAuto } from "../../services/C4S Decommission - Replacement/createWorkTicketAuto";
import { generate } from "shortid";
import useOutsideClick from "../../customHook/use-outside-click";
import { getWorkticketVehiclesAndAssetsList } from "../../services/C4S Decommission - Replacement/getWorkTicketVINList";
import { getAllReasonsData } from "../../services/C4S Decommission - Replacement/getAllReasonsData";

const tableHeader = [
  {
    name: "Bundle",
    width: "6%",
  },
  {
    name: "Subscription",
    width: "7%",
  },
  {
    name: "Subscription ID",
    width: "5%",
  },
  {
    name: "Tech Ref ID",
    width: "7%",
  },
  {
    name: "Hardware",
    width: "12%",
  },
  {
    name: "Requested",
    width: "5%",
  },
  {
    name: "ATP",
    width: "5%",
  },
];
export default function SplitByMaterialView(props) {
  const { data: { ticketId, salesOrg, addressId, contactId } = {} } = props;
  const [loader, setLoader] = React.useState(false);
  const [tableData, setTableData] = React.useState([]);
  const [selectedRowCheckbox, setSelectedRowCheckbox] = React.useState([]);
  const [vehicleDetails, setVehicleDetails] = React.useState({});

  const enrichedRef = useRef(false);

  const cspSessionData = JSON.parse(sessionStorage.getItem("CSPDetails"));
  const salesOrgId = cspSessionData?.CSP?.length ? cspSessionData?.CSP.filter((csp) => csp.DefaultCSP === "1")[0].Opp_Sales_Org : "";
  const { notification, source, isSourceCSP } = useContext(C4SContext);

  const handleClickOutside = () => {
    notification.setNotification(null);
  };

  const clickRef = useOutsideClick(handleClickOutside);

  const notify = useCallback((content, type) => {
    notification.setNotification(<SLNotification key={generate()} type={type} title={content} fullBleed={true} ref={clickRef} />);
  });

  useEffect(() => {
    let materialList = [];
    setLoader(true);
    const getInitialData = async () => {
      try {
        // featching materialList for a given workticket
        let workticketMaterialList = await getWorkTicketMaterialList(ticketId);
        // fetching vehicles and assets of the given workticket
        let vehiclesAndAssets = await getWorkticketVehiclesAndAssetsList(ticketId);
        if (vehiclesAndAssets?.status === 200 && vehiclesAndAssets?.data) {
          vehiclesAndAssets = vehiclesAndAssets.data.data.vehicles.concat(vehiclesAndAssets.data.data.assets);
        }
        if (workticketMaterialList?.status === 200 && workticketMaterialList?.data) {
          workticketMaterialList = workticketMaterialList.data.data;
          materialList = workticketMaterialList?.map((material, idx) => {
            // find the vehicldeids in which material exists
            const matchingVins = vehiclesAndAssets.filter((vin) => {
              return vin.subscriptions?.some(
                (sub) => sub.hardwareId === material.materialId && sub.techRefId === material.techRefId && sub.subscriptionId === material.sapId
              );
            });
            return {
              subscription: material.contractName,
              requested: material.totalQty,
              subscriptionId: material.sapId,
              vinIds: matchingVins.map((v) => v.id),
              atpKey: idx + 1,
              ...material,
            };
          });

          setTableData(materialList);
          const today = new Date();
          const requestedDeliveryDate = new Date(today);
          requestedDeliveryDate.setDate(today.getDate() + 7);
          let itemsList = materialList.map((material) => {
            return {
              itemNo: material.atpKey,
              itemCode: material.materialId,
              // itemDescription: material.materialName,
              orderQuantity: material.requested,
              unitOfMeasurement: material.unitOfMeasure,
              requestedDeliveryDate: moment(requestedDeliveryDate).format("YYYYMMDD"),
            };
          });
          let data = {
            shipToParty: cspSessionData?.ERP_Partner_Number,
            salesOrganization: salesOrgId,
            distributionChannel: cspSessionData?.distributionChannel,
            division: cspSessionData?.division,
            items: itemsList,
          };
          // featching ATP for the materialList
          let response = await ATPCheck(data);
          if (response?.status === 200) {
            const responseData = response.data.data.items;
            let materialData = materialList.map((material) => {
              const { confirmedQuantity = 0 } = responseData.find((res) => Number(res.itemNo) === Number(material.atpKey)) || {};
              return { ...material, atp: confirmedQuantity };
            });
            setTableData(materialData);
          } else {
            setTableData(materialList.map((i) => ({ ...i, atp: 0 })));
            notify("Unable to fetch ATP details for the material list due to an unexpected error", "error");
          }
          // featching vehicle details for the materialList using bundleID
          const bundleIds = [...new Set(materialList.map((material) => material.bundleId))].join(",");
          const vehicleDetails = await ViewVehicleList(bundleIds, addressId, contactId, cspSessionData?.CSP[0].CRM_Ref_ID, null, null, true);
          if (vehicleDetails?.status === 200 && vehicleDetails?.data) {
            setVehicleDetails(vehicleDetails?.data?.data.vehicles || {});
          }
        } else {
          setTableData([]);
          notify("Unable to fetch material list due to an unexpected error", "error");
        }
      } catch (error) {
        setTableData([]);
        notify("Unable to fetch material list due to an unexpected error", "error");
      }
      setLoader(false);
    };
    getInitialData();
  }, []);

  useEffect(() => {
    if (enrichedRef.current) return;
    if (!tableData.length || !Object.keys(vehicleDetails).length) return;

    setTableData((prev) => {
      //  verify the logic. does lineType change based on different vehicle? can i use vehicle id present in row already
      let changed = false;
      const updated = prev.map((row) => {
        if (row.lineType) return row; // already enriched
        const vehiclesForBundle = vehicleDetails[row.bundleId] || [];
        for (const veh of vehiclesForBundle) {
          const mappings = veh.materialMapping || [];
          const match = mappings.find(
            (m) =>
              String(m.subscriptionId) === String(row.subscriptionId) &&
              String(m.hardwareId) === String(row.materialId) &&
              String(m.techRefId) === String(row.techRefId)
          );
          if (match) {
            return { ...row, lineType: match.lineType };
          }
        }
        return row;
      });
      if (!changed) enrichedRef.current = true;
      return changed ? updated : prev;
    });
  }, [vehicleDetails, tableData.length]);

  const formattedTableData = useMemo(() => {
    // Sort by bundleId, subscriptionId, techRefId (numeric-aware lexicographic)
    const sorted = [...tableData].sort((a, b) => {
      const cmpBundle = String(a.bundleId).localeCompare(String(b.bundleId), undefined, { numeric: true, sensitivity: "base" });
      if (cmpBundle !== 0) return cmpBundle;
      const cmpSub = String(a.subscriptionId).localeCompare(String(b.subscriptionId), undefined, { numeric: true, sensitivity: "base" });
      if (cmpSub !== 0) return cmpSub;
      return String(a.techRefId).localeCompare(String(b.techRefId), undefined, { numeric: true, sensitivity: "base" });
    });
    return sorted;
  }, [tableData]);

  const handleCheckboxChange = useCallback((rowData) => {
    const { bundleId, subscriptionId, materialId, techRefId } = rowData;
    setSelectedRowCheckbox((prev) => {
      const key = `${bundleId}-${subscriptionId}-${techRefId}-${materialId}`;
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      } else {
        return [...prev, key];
      }
    });
  }, []);

  const handleSplitClick = useCallback(async () => {
    let reasonsResponse = await getAllReasonsData("WTA_CNC_WT");
    reasonsResponse = reasonsResponse.data?.data?.filter((data) => data.code === "CBC")[0];
    let cspAccDetails = cspSessionData?.CSP?.filter((cspDetail) => cspDetail.DefaultCSP === "1")[0] || "";
    let salesOrgNumber = salesOrg.split(" ")[0];
    let payload = {
      cspId: cspAccDetails?.CSPID || "",
      workticketId: ticketId ?? "",
      cancellationReason: reasonsResponse.description,
      cancellationReasonCode: reasonsResponse.subCode,
      //createdBy: `${cspDetails.firstName} ${cspDetails.lastName}` ?? "",
      salesOrg: salesOrgNumber ?? "",
      erpTicketType: source.source === "CSP" ? "ZINS" : "ZSRQ",
      modifiedBy: `${cspSessionData?.firstName} ${cspSessionData?.lastName}`,
      modifiedByEmail: cspSessionData?.email || "",
      modifiedByEid: cspSessionData?.Employee_ID || "",
      assignedTo:
        cspSessionData?.source === "CSP"
          ? cspSessionData?.CSP.length
            ? cspSessionData?.CSP.filter((csp) => csp?.DefaultCSP === "1")[0].CSPOwner
            : null
          : cspSessionData?.CASEDetails.length
          ? cspSessionData?.CASEDetails.filter((cases) => cases.DefaultCase === "1")[0].CaseOwner
          : null,
      assignedToEmail:
        cspSessionData?.source === "CSP"
          ? cspSessionData?.CSP.length
            ? cspSessionData?.CSP.filter((csp) => csp?.DefaultCSP === "1")[0].CSPOwnerEmail
            : null
          : cspSessionData?.CASEDetails.length
          ? cspSessionData?.CASEDetails.filter((cases) => cases.DefaultCase === "1")[0].CaseOwnerEmail
          : null,
      assignedToEid:
        cspSessionData?.source === "CSP"
          ? cspSessionData?.CSP.length
            ? cspSessionData?.CSP.filter((csp) => csp?.DefaultCSP === "1")[0].CSPOwnerEId
            : null
          : cspSessionData?.CASEDetails.length
          ? cspSessionData?.CASEDetails.filter((cases) => cases.DefaultCase === "1")[0].CaseOwnerEId
          : null,
    };
    let missingFieldsArray = [];
    for (const key of Object.keys(payload)) {
      if (key === "workticketId" || key === "salesOrg") {
        if (payload[key] === "" || payload[key] === undefined || payload[key] === null) {
          missingFieldsArray.push(key);
        }
      }
    }
    if (missingFieldsArray.length > 0) {
      return setTimeout(() => {
        notify(
          "Unable to cancel work ticket due to missing information. Kindly contact IT team for support. Missing information: " +
            missingFieldsArray.toString() +
            ".",
          "error",
          false
        );
      }, 10);
    }
    let response = await cancelWorkticket(payload); // as of now getting 500

    if (response.status === 200 && response?.data?.statusCode === "200" && response.data?.data?.status === "Cancelled") {
      let createWorkTicketPayload = {
        createdBy: `${cspSessionData?.firstName} ${cspSessionData?.lastName}`,
        createdByEmail: cspSessionData?.email,
        createdByEid: cspSessionData?.Employee_ID,
        assignTo: cspSessionData?.Employee_ID,
        assignedTo: `${cspSessionData?.firstName} ${cspSessionData?.lastName}`,
        assignedToEmail: cspSessionData?.email,
        assignedToEid: cspSessionData?.CSP[0].CSPOwnerEId || "",
        sourceWorkticket: null,
        requestDate: moment(new Date()).format("YYYYMMDD"),
        crmReferenceId: cspSessionData?.CSP?.[0]?.CRM_Ref_ID || "",
        currencyCode: cspSessionData?.currencyCode || "USD",
        documentType: cspSessionData?.CSP[0].CSPID !== "" ? "ZINS" : "ZSRQ",
        documentDate: moment(new Date()).format("YYYYMMDD"),
        salesOrg: salesOrgId ? salesOrgId : "",
        distributionChannel: cspSessionData?.distributionChannel || "02",
        division: cspSessionData?.division || "02",
        contractAccount: cspSessionData?.CONTRACT_ACCOUNT_ID?.[0] || "",
        erpVendorId: "",
        erpBusinessPartnerId: cspSessionData?.ERP_Partner_Number || "",
        erpTicketType: cspSessionData?.CSP[0].CSPID !== "" ? "ZINS" : "ZSRQ",
        erpProductRecipientPartyId: cspSessionData?.ResellerBusinessPartnerNumber || cspSessionData?.ERP_Partner_Number || "",
        salesRepId: cspSessionData?.Employee_ID,
        subStatus: "",
        oldDeliveryNo: "",
        providerOrder: cspSessionData?.CSP[0].Provider_Order || "",
        notesForWarehouse: "Work Ticket Request",
        customerName: cspSessionData?.AccountName,
        customerNumber: cspSessionData?.AccountNo,
        cspId: cspSessionData?.CSP[0].CSPID || "",
        caseId:
          cspSessionData?.source?.toLowerCase() !== "csp" && cspSessionData.CASEDetails.length
            ? cspSessionData.CASEDetails.filter((cases) => cases.DefaultCase === "1")[0].CaseSFDCID
            : "",
        caseNumber:
          cspSessionData?.source?.toLowerCase() !== "csp" && cspSessionData.CASEDetails.length
            ? cspSessionData?.CASEDetails.filter((cases) => cases.DefaultCase === "1")[0]?.CaseNumber
            : "",
        accountId: cspSessionData?.AccountID,
        resellerBusinessPartnerNumber: cspSessionData?.ResellerBusinessPartnerNumber || cspSessionData?.ERP_Partner_Number || "", // ResellerBusinessPartnerNumber
        erpPrimaryContactNumber: cspSessionData?.ERP_Primary_Contact_NUmber ?? "",
        accountName: cspSessionData?.AccountName,
        workTicketType: "Outbound",
      };
      //checked subscription details
      let vinMaterialsRequest = []; //two object one for selected rows and another for non selected rows
      //seelcted rows
      const selectedMaterialList = tableData.filter((mat) =>
        selectedRowCheckbox.includes(`${mat.bundleId}-${mat.subscriptionId}-${mat.techRefId}-${mat.materialId}`)
      );
      const selectedVehicleList = uniqBy(
        selectedMaterialList.reduce((acc, cur) => {
          const { bundleId, vinIds } = cur;
          const idSet = new Set(vinIds);
          //VinIDs contain the vehicle ids belong to the existing workticket
          const vehicle = vehicleDetails[bundleId].filter((v) => idSet.has(Number(v.id)));
          if (vehicle && vehicle.length) acc.push(...vehicle);
          return acc;
        }, []),
        "id"
      );
      // NEW: add selectedMaterialIds (only materialIds from selectedMaterialList that belong to this vehicle)
      const selectedMaterialKeySet = new Set(selectedMaterialList.map((m) => `${m.subscriptionId}:${m.materialId}:${m.techRefId}`));
      const enrichedSelectedVehicle = selectedVehicleList.map((v) => {
        const ids = (v.materialMapping || [])
          .filter((mm) => selectedMaterialKeySet.has(`${mm.subscriptionId}:${mm.hardwareId}:${mm.techRefId}`))
          .map((mm) => mm.id);
        return { ...v, selectedMaterialIds: ids };
      });

      //non checked subscription details
      const notSelectedMaterialList = tableData.filter(
        (mat) => !selectedRowCheckbox.includes(`${mat.bundleId}-${mat.subscriptionId}-${mat.techRefId}-${mat.materialId}`)
      );
      const nonSelectedVehicleList = uniqBy(
        notSelectedMaterialList.reduce((acc, cur) => {
          const { bundleId, vinIds } = cur;
          const idSet = new Set(vinIds);
          const vehicle = vehicleDetails[bundleId].filter((v) => idSet.has(Number(v.id)));
          if (vehicle && vehicle.length) acc.push(...vehicle);
          return acc;
        }, []),
        "id"
      );

      // NEW: add selectedMaterialIds (only materialIds from notSelectedMaterialList that belong to this vehicle)
      const nonSelectedMaterialKeySet = new Set(notSelectedMaterialList.map((m) => `${m.subscriptionId}:${m.materialId}:${m.techRefId}`));
      const enrichedNonSelectedVehicle = nonSelectedVehicleList.map((v) => {
        const ids = (v.materialMapping || [])
          .filter((mm) => nonSelectedMaterialKeySet.has(`${mm.subscriptionId}:${mm.hardwareId}:${mm.techRefId}`))
          .map((mm) => mm.id);
        return { ...v, selectedMaterialIds: ids };
      });

      let itemNo1 = 0;
      let itemNo2 = 0;

      vinMaterialsRequest.push(
        {
          addressId: addressId,
          contactId: contactId,
          vins: enrichedSelectedVehicle.map((v) => ({
            id: v.id,
            vinStatus: v.vehicleStatus,
            type: v.deviceType,
            vinMaterials: v.selectedMaterialIds.join(", "), // passing db ids of selected materials only
          })),

          materials: selectedMaterialList.map((m) => {
            itemNo1 += 10;
            return {
              id: m.mid,
              materialId: m.materialId,
              materialName: m.materialName,
              summaryQty: m.totalQty, // passing count as present in response
              materialLineId: String(itemNo1)?.padStart(6, "0"),
              unitMeasure: m.unitOfMeasurement || "EA",
              techRefId: m.techRefId,
              processingCode: m.lineType === "Reship" ? getExchangeCode(m.processingCode) : m.processingCode,
              installedQuantity: "",
              chargePrice: !isSourceCSP.isSourceCSP ? m?.chargeAmt : 0, // chargeAmt does not come as part of api response
              family: "",
              customerCharge: "",
              serviceProcessType: "",
              invoiceRelevant: "",
              customerPONumber: cspSessionData?.CSP_CUstomer_PO_Number,
              customerPONumberKut: cspSessionData?.CSP_CUstomer_PO_Number,
              baseItemId: m.bundleId,
              esnToBeReturned: m?.esnToBeReturned ? m.esnToBeReturned : "", //does not come as part of api response
              sapId: m.sapId,
              contractId: m.cid,
              contactName: m.contractName,
              orderType: m.orderType,
              contractOverride: m.contractOverride,
            };
          }),
        },
        {
          addressId: addressId,
          contactId: contactId,
          vins: enrichedNonSelectedVehicle.map((v) => ({
            id: v.id,
            vinStatus: v.vehicleStatus,
            type: v.deviceType,
            vinMaterials: v.selectedMaterialIds.join(", "), // list of material db id that are present in material List
          })),
          materials: notSelectedMaterialList.map((m) => {
            itemNo2 += 10;
            return {
              id: m.mid,
              materialId: m.materialId,
              materialName: m.materialName,
              summaryQty: m.totalQty,
              materialLineId: String(itemNo2)?.padStart(6, "0"),
              unitMeasure: m.unitOfMeasurement || "EA",
              techRefId: m.techRefId,
              processingCode: m.lineType === "Reship" ? getExchangeCode(m.processingCode) : m.processingCode,
              installedQuantity: "",
              chargePrice: !isSourceCSP.isSourceCSP ? m?.chargeAmt : 0,
              family: "",
              customerCharge: "",
              serviceProcessType: "",
              invoiceRelevant: "",
              customerPONumber: cspSessionData?.CSP_CUstomer_PO_Number,
              customerPONumberKut: cspSessionData?.CSP_CUstomer_PO_Number,
              baseItemId: m.bundleId,
              esnToBeReturned: m?.esnToBeReturned ? m.esnToBeReturned : "",
              sapId: m.sapId,
              contractId: m.cid,
              contactName: m.contractName,
              orderType: m.orderType,
              contractOverride: m.contractOverride,
            };
          }),
        }
      );

      createWorkTicketPayload.vinMaterialsRequest = vinMaterialsRequest;
      let responseCreateTicket = await CreateWorkTicketAuto(createWorkTicketPayload);
      if (responseCreateTicket.status === 200 && responseCreateTicket?.data?.statusCode === "200") {
        notify("Split by material operation is completed successfully", "success");
      }
    } else {
      notify("We are currently experiencing issues, please try again", "error");
      return;
    }
    switchTab(3);
  }, [
    tableData,
    selectedRowCheckbox,
    vehicleDetails,
    isSourceCSP,
    cspSessionData,
    salesOrg,
    salesOrgId,
    source?.source,
    addressId,
    contactId,
    notify,
    ticketId,
  ]);

  return (
    <>
      <Spacer top="16px" />
      <Title>Split By Material</Title>
      <Spacer top="8px" />
      <MediumText>Please select the material(s) below to be shipped out.</MediumText>
      <Spacer bottom="12px" />
      <div style={{ display: "flex", justifyContent: "flex-end", direction: "row" }}>
        {/* redirect to workticket history tab */}
        <Button secondary style={{ marginRight: "16px" }} onClick={() => switchTab(3)}>
          Cancel
        </Button>
        {/* cancel current and create 2 on non checked and checked rows */}
        <Button primary disabled={!selectedRowCheckbox.length} onClick={() => handleSplitClick()}>
          Split
        </Button>
      </div>
      <Spacer bottom="16px" />
      <Loader active={loader} fullscreen={false} />
      {!loader && tableData.length === 0 && <SmallText>No materials found for this work ticket.</SmallText>}
      {!loader && tableData.length > 0 && (
        <table style={{ borderSpacing: "0px", fontFamily: "Verizon-NHG-eTX, Helvetica, Arial, sans-serif" }}>
          <thead>
            <tr style={{ textAlign: "left" }}>
              {tableHeader.map((header) => (
                <th key={header.name} style={{ width: header.width, padding: "10px", borderBottom: "1px solid black" }}>
                  <SmallText bold>{header.name}</SmallText>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {formattedTableData.map((row, rowIdx) => {
              const { bundleId, subscriptionId, requested, atp, techRefId, materialId } = row;
              rowIdx += 1;
              let disableCheckbox = Number(atp) < Number(requested);
              return (
                <tr key={`${bundleId}-${subscriptionId}`} style={{ backgroundColor: rowIdx % 2 !== 0 ? "#F6F6F6" : "#D9D9D9", verticalAlign: "top" }}>
                  <td style={{ width: "6%", padding: "10px" }}>
                    <div style={{ paddingLeft: disableCheckbox ? "30px" : "0px" }}>
                      {!disableCheckbox ? (
                        <input
                          type="checkbox"
                          style={{ width: "20px", height: "20px", marginRight: "8px" }}
                          checked={selectedRowCheckbox.includes(`${bundleId}-${subscriptionId}-${techRefId}-${materialId}`)}
                          onChange={() => handleCheckboxChange(row)}
                        />
                      ) : (
                        ""
                      )}
                      {bundleId}
                    </div>
                  </td>
                  <td style={{ width: "7%", padding: "10px" }}>{row.subscription}</td>
                  <td style={{ width: "5%", padding: "10px" }}>{subscriptionId}</td>
                  <td style={{ width: "7%", padding: "10px" }}>{techRefId}</td>
                  <td style={{ width: "12%", padding: "10px" }}>
                    {row.materialName} - {row.materialId}
                  </td>
                  <td style={{ width: "5%", padding: "10px" }}>{row.requested}</td>
                  <td style={{ width: "5%", padding: "10px" }}>{Number(row.atp)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

export const SplitByMaterialViewMetadata = {
  name: "Split By Material View",
  id: "Split_By_Material_View",
  component: lazy(() => import("./SplitByMaterialView")),
  route: "/split_by_material_view",
};
